import Long from 'long';
import { IllegalArgumentException } from './lang/illegalargumentexception';
import { NullPointerException } from './lang/nullpointerexception';

export abstract class TagInRecord {
  public mDone = false;
  public mParent: TagInRecord | null = null;
  public mPos: number;
  public mTagId: number;
  public mLen = 0;

  protected constructor(tagId: number, pos: number) {
    this.mTagId = tagId;
    this.mPos = pos;
  }
}

export class CompositeTagInRecord extends TagInRecord {
  public mChildTags: Array<TagInRecord> | null = null;

  public constructor(tagId: number, pos: number) {
    super(tagId, pos);
    //console.log('Composite', this.mTagId);
  }

  public addTagInRecord(tag: TagInRecord): void {
    if (tag === undefined) {
      throw new IllegalArgumentException('Undefined tag can not be a child');
    }
    if (tag === null) {
      throw new NullPointerException('Null tag is not suppsed to add');
    }
    if (this.mChildTags === null) {
      this.mChildTags = new Array<TagInRecord>();
    }
    tag.mParent = this;
    this.mChildTags.push(tag);
  }

  public getChildCount(): number {
    if (this.mChildTags === null) {
      return 0;
    }
    return this.mChildTags.length;
  }
}

export class CardTagInRecord extends TagInRecord {
  public mCard: Long = Long.ZERO;
  public constructor(tagId: number, pos: number) {
    super(tagId, pos);
    //console.log('Card', this.mTagId);
  }
}

export class RealTagInRecord extends TagInRecord {
  public mReal = 0;
  public mRealBytes: ArrayBuffer = new ArrayBuffer(0);
  public constructor(tagId: number, pos: number) {
    super(tagId, pos);
    //console.log('Real', this.mTagId);
  }
}

export class BinaryTagInRecord extends TagInRecord {
  public mBinSize: Long = Long.ZERO;
  public mBinData: ArrayBuffer = new ArrayBuffer(0);
  public constructor(tagId: number, pos: number) {
    super(tagId, pos);
    //console.log('binary', this.mTagId);
  }
}

export enum TagType {
  kComposite = 0, //0x0,  // Most significant bit 00
  kCard = 1, //0x40,		// Most significant bit 01
  kReal = 2, //0x80,		// Most significant bit 10
  kBinary = 3, //0xc0		// Most significant bit 11
}

export function getTagType(inTagId: number): TagType {
  return inTagId >> 6;
}

export function InflateTag(data: DataView, ioPos: number, depth: number): TagInRecord | null {
  if (ioPos >= data.byteLength) {
    return null;
  }
  const startPos = ioPos;
  const tagId = data.getUint8(ioPos);
  const tagType = getTagType(tagId);
  ioPos++;
  switch (tagType) {
    case TagType.kComposite: {
      const card = InVarCard(data, ioPos);
      ioPos += card.consumed;
      const rec = new CompositeTagInRecord(tagId, startPos);
      //rec.mPos = startPos;
      rec.mTagId = tagId;

      for (let i = new Long(0, 0, true); i.compare(card.result) < 0; i = i.add(1)) {
        const childRecord = InflateTag(data, ioPos, depth + 1);
        if (childRecord !== null) {
          rec.addTagInRecord(childRecord);
          ioPos += childRecord.mLen;
        } else {
          break;
        }
      }
      rec.mLen = ioPos - startPos;
      return rec;
    }
    case TagType.kCard: {
      const card = InVarCard(data, ioPos);
      ioPos += card.consumed;
      const cardTag = new CardTagInRecord(tagId, startPos);
      cardTag.mCard = card.result;
      cardTag.mLen = ioPos - startPos;
      cardTag.mTagId = tagId;
      return cardTag;
    }
    case TagType.kReal: {
      const dataBytes = data.buffer.slice(ioPos, ioPos + 8) as ArrayBuffer;
      const real = toLongReal(dataBytes);
      ioPos += 8;
      const realTag = new RealTagInRecord(tagId, startPos);
      realTag.mRealBytes = dataBytes;
      realTag.mReal = real;
      realTag.mTagId = tagId;
      realTag.mPos = startPos;
      realTag.mLen = ioPos - startPos;

      return realTag;
    }
    case TagType.kBinary: {
      const card = InVarCard(data, ioPos);
      ioPos += card.consumed;
      const binTag = new BinaryTagInRecord(tagId, startPos);
      binTag.mBinSize = card.result;
      binTag.mTagId = tagId;
      binTag.mBinData = data.buffer.slice(ioPos, ioPos + card.result.toInt()) as ArrayBuffer;
      ioPos += card.result.toInt();
      binTag.mLen = ioPos - startPos;
      return binTag;
    }
    default: {
      console.error('Could not detect the tag type');
      return null;
    }
  }
}

function toLongReal(buf: ArrayBuffer): number {
  //for(let i=0; i< buf.byteLength; i++){
  //	uintBuf[buf.byteLength - 1 - i] = buf[i];
  //}
  const dv = new DataView(buf);
  const num = dv.getFloat64(0, true);
  //console.log(num);
  return num;
}

export function InVarCard(buf: DataView, pos: number) {
  let outLength = 0;
  let result = new Long(0, 0, true);
  for (let i = 0; i < 10; i++) {
    const value = new Long(buf.getUint8(pos), 0, true);
    result = result.or(value.and(0x7f).shiftLeft(7 * i));
    pos++;
    outLength++;
    if (value.and(0x80).isZero()) {
      break;
    }
  }
  return { consumed: outLength, result: result };
}

export function InInflate(data: ArrayBuffer): TagInRecord | null {
  const pos = 0;
  return InflateTag(new DataView(data), pos, 0);
}
